import React from 'react';
import './Security.css';

export default function Security() {
  return (
    <div className="security-container">
      <header className="security-headers">
        <h1>Security</h1>
        <p className="last-updated">Protecting Your Data is Our Priority</p>
      </header>

      <div className="security-content">
        <section className="security-section">
          <h2>1. Our Commitment to Security</h2>
          <p>
            At Humanex, security is not just a feature—it's the foundation of everything we do. We understand that you trust us with your organization's most sensitive information, and we take that responsibility seriously. Our comprehensive security program is designed to protect your data against unauthorized access, disclosure, alteration, and destruction.
          </p>
        </section>

        <section className="security-section">
          <h2>2. Data Encryption</h2>
          <h3>2.1 Encryption in Transit</h3>
          <p>
            All data transmitted between your browser and our servers is protected using Transport Layer Security (TLS) 1.3 encryption with 256-bit encryption keys. This ensures that your data cannot be intercepted or read by unauthorized parties during transmission.
          </p>
          
          <h3>2.2 Encryption at Rest</h3>
          <p>
            Your data is encrypted at rest using AES-256 encryption, the same standard used by banks and government agencies worldwide. All databases, backups, and file storage systems employ encryption to ensure your data remains secure even in the unlikely event of physical media theft.
          </p>

          <h3>2.3 Key Management</h3>
          <p>
            We use industry-leading key management services to securely generate, store, and rotate encryption keys. Keys are stored separately from encrypted data and are accessible only to authorized systems and personnel.
          </p>
        </section>

        <section className="security-section">
          <h2>3. Infrastructure Security</h2>
          <h3>3.1 Secure Cloud Infrastructure</h3>
          <p>
            Humanex is hosted on enterprise-grade cloud infrastructure with world-class security certifications including SOC 2 Type II, ISO 27001, and PCI DSS compliance. Our infrastructure partners maintain physically secure data centers with 24/7 monitoring, biometric access controls, and redundant power and network systems.
          </p>

          <h3>3.2 Network Security</h3>
          <p>
            Our network architecture includes multiple layers of protection: dedicated firewalls, intrusion detection and prevention systems (IDS/IPS), distributed denial-of-service (DDoS) protection, and network segmentation to isolate sensitive systems. All network traffic is continuously monitored for suspicious activity.
          </p>

          <h3>3.3 Regular Security Audits</h3>
          <p>
            We conduct regular internal and third-party security audits, vulnerability assessments, and penetration testing to identify and remediate potential security weaknesses. Our security posture is continuously evaluated and improved based on these findings.
          </p>
        </section>

        <section className="security-section">
          <h2>4. Access Control and Authentication</h2>
          <h3>4.1 Multi-Factor Authentication (MFA)</h3>
          <p>
            We strongly recommend and support multi-factor authentication for all user accounts. MFA adds an additional layer of security by requiring users to verify their identity using a second factor beyond just a password, such as a mobile app, SMS code, or hardware token.
          </p>

          <h3>4.2 Role-Based Access Control (RBAC)</h3>
          <p>
            Our platform implements granular role-based access controls, allowing you to define exactly what each user can see and do within the system. Users are granted the minimum level of access necessary to perform their job functions, following the principle of least privilege.
          </p>

          <h3>4.3 Single Sign-On (SSO)</h3>
          <p>
            We support enterprise single sign-on (SSO) integration with major identity providers including Okta, Azure AD, Google Workspace, and others. SSO enables centralized authentication management and enhanced security through your organization's existing identity infrastructure.
          </p>

          <h3>4.4 Session Management</h3>
          <p>
            User sessions are protected with secure, encrypted tokens that expire after periods of inactivity. Automatic logout features help prevent unauthorized access when users leave their devices unattended.
          </p>
        </section>

        <section className="security-section">
          <h2>5. Application Security</h2>
          <h3>5.1 Secure Development Lifecycle</h3>
          <p>
            Security is integrated into every phase of our development process. Our engineering team follows secure coding practices, conducts code reviews with security focus, and uses automated security scanning tools to identify vulnerabilities before code reaches production.
          </p>

          <h3>5.2 Security Testing</h3>
          <p>
            We perform regular security testing including: static application security testing (SAST), dynamic application security testing (DAST), software composition analysis (SCA) for third-party dependencies, and manual penetration testing by certified security professionals.
          </p>

          <h3>5.3 Input Validation and Sanitization</h3>
          <p>
            All user input is validated and sanitized to prevent injection attacks such as SQL injection, cross-site scripting (XSS), and other common web vulnerabilities. We employ multiple layers of defense including parameterized queries, input encoding, and content security policies.
          </p>
        </section>

        <section className="security-section">
          <h2>6. Data Protection and Privacy</h2>
          <h3>6.1 Data Isolation</h3>
          <p>
            Each customer's data is logically isolated within our systems to prevent any cross-contamination or unauthorized access between different organizations. Your data is yours alone and is never shared with other customers.
          </p>

          <h3>6.2 Data Residency</h3>
          <p>
            We offer data residency options to ensure your data is stored in geographic locations that meet your compliance requirements. You can choose where your data is hosted and processed to comply with local data protection regulations.
          </p>

          <h3>6.3 Data Backup and Recovery</h3>
          <p>
            Your data is automatically backed up multiple times daily to geographically distributed locations. All backups are encrypted and tested regularly to ensure rapid recovery in case of data loss or disaster. We maintain a comprehensive disaster recovery plan with defined recovery time objectives (RTO) and recovery point objectives (RPO).
          </p>

          <h3>6.4 Data Retention and Deletion</h3>
          <p>
            We retain your data only as long as necessary to provide our services or as required by law. When you request data deletion, we permanently remove your data from our active systems and backups according to our data retention policy.
          </p>
        </section>

        <section className="security-section">
          <h2>7. Compliance and Certifications</h2>
          <p>
            Humanex maintains compliance with major security and privacy standards including:
          </p>
          <ul>
            <li>SOC 2 Type II - Demonstrating effective security controls and practices</li>
            <li>ISO 27001 - International standard for information security management</li>
            <li>GDPR - European Union General Data Protection Regulation compliance</li>
            <li>CCPA - California Consumer Privacy Act compliance</li>
            <li>HIPAA - Health Insurance Portability and Accountability Act (for applicable features)</li>
            <li>PCI DSS - Payment Card Industry Data Security Standard (for payment processing)</li>
          </ul>
          <p>
            We undergo regular third-party audits to maintain these certifications and can provide compliance documentation upon request.
          </p>
        </section>

        <section className="security-section">
          <h2>8. Employee Security</h2>
          <h3>8.1 Background Checks</h3>
          <p>
            All employees with access to customer data undergo comprehensive background checks as permitted by applicable law. Our hiring process includes verification of identity, employment history, and criminal background where legally permissible.
          </p>

          <h3>8.2 Security Training</h3>
          <p>
            Every employee receives mandatory security awareness training upon joining and regularly thereafter. Training covers topics including data protection, secure coding practices, social engineering awareness, incident response procedures, and regulatory compliance requirements.
          </p>

          <h3>8.3 Access Management</h3>
          <p>
            Employee access to customer data is strictly controlled and monitored. Access is granted only when necessary for job functions and is regularly reviewed and audited. All access to customer data is logged and monitored for anomalous activity.
          </p>

          <h3>8.4 Confidentiality Agreements</h3>
          <p>
            All employees and contractors sign strict confidentiality and data protection agreements as a condition of employment, ensuring they understand their responsibilities regarding customer data protection.
          </p>
        </section>

        <section className="security-section">
          <h2>9. Incident Response and Monitoring</h2>
          <h3>9.1 24/7 Security Monitoring</h3>
          <p>
            Our security operations center monitors our systems around the clock for potential security incidents, anomalous behavior, and system health. Automated alerting systems notify our security team of suspicious activities in real-time.
          </p>

          <h3>9.2 Incident Response Plan</h3>
          <p>
            We maintain a comprehensive incident response plan that defines procedures for detecting, containing, investigating, and remediating security incidents. Our incident response team is trained to respond quickly and effectively to minimize any potential impact.
          </p>

          <h3>9.3 Breach Notification</h3>
          <p>
            In the unlikely event of a security breach affecting your data, we will notify you promptly in accordance with applicable laws and regulations. Our notification will include details about the incident, affected data, remediation steps taken, and recommendations for protecting your information.
          </p>

          <h3>9.4 Audit Logging</h3>
          <p>
            Comprehensive audit logs track all access to and modifications of customer data. Logs include timestamps, user identities, actions performed, and IP addresses. These logs are securely stored, regularly reviewed, and available for compliance audits.
          </p>
        </section>

        <section className="security-section">
          <h2>10. Vendor and Third-Party Security</h2>
          <p>
            We carefully vet all third-party service providers and vendors who may have access to customer data. Our vendor management program includes security assessments, contractual data protection requirements, regular security reviews, and ongoing monitoring to ensure our vendors maintain appropriate security standards.
          </p>
        </section>

        <section className="security-section">
          <h2>11. Physical Security</h2>
          <p>
            While Humanex is a cloud-based service, our office facilities and any physical infrastructure we control implement robust physical security measures including: secured access with badge readers and biometric authentication, 24/7 video surveillance, visitor management and escort policies, and secure disposal procedures for hardware and media.
          </p>
        </section>

        <section className="security-section">
          <h2>12. Your Security Responsibilities</h2>
          <p>
            Security is a shared responsibility. To help protect your account and data, we recommend:
          </p>
          <ul>
            <li>Enable multi-factor authentication for all users</li>
            <li>Use strong, unique passwords and change them regularly</li>
            <li>Limit user access based on job responsibilities</li>
            <li>Regularly review user accounts and remove access for former employees</li>
            <li>Train your team on security best practices and phishing awareness</li>
            <li>Keep your devices and software up to date with security patches</li>
            <li>Report any suspicious activity or potential security issues immediately</li>
          </ul>
        </section>

        <section className="security-section">
          <h2>13. Continuous Improvement</h2>
          <p>
            Security threats evolve constantly, and so do our defenses. We continuously monitor the threat landscape, invest in new security technologies, update our security practices based on industry best practices and emerging threats, and participate in security communities and information sharing programs to stay ahead of potential risks.
          </p>
        </section>

        <section className="security-section">
          <h2>14. Security Contact</h2>
          <p>
            We take security reports seriously. If you discover a security vulnerability or have security concerns, please contact us immediately:
          </p>
          <div className="contact-info">
            <p><strong>Security Team Email:</strong> support@devopstrioglobal.com</p>
            <p><strong>Emergency Hotline:</strong> +1 (555) 123-4567 (24/7)</p>
            <p><strong>Bug Bounty Program:</strong> Visit our responsible disclosure program at Humanex.com/security/disclosure</p>
          </div>
          <p>
            For general inquiries about our security practices or to request security documentation such as SOC 2 reports or penetration test summaries, please contact our compliance team at compliance@Humanex.com.
          </p>
        </section>
      </div>

      {/* <footer className="security-footer">
        <p>© 2025 Humanex. All rights reserved.</p>
      </footer> */}
    </div>
  );
}